import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, onSnapshot, query, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { FaPlus, FaPencilAlt, FaTrash, FaSearch, FaTimes, FaUserPlus, FaUserEdit, FaFilePdf, FaSpinner } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import Modal from 'react-modal';
import { motion, AnimatePresence } from 'framer-motion';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ClientesPDF from './components/ClientesPDF'; 

const customModalStyles = { content: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', border: 'none', background: 'transparent', width: '90%', maxWidth: '500px', padding: '0' }, overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1050 }};

// --- COMPONENTE BOTON PDF ---
const BotonDescargaPDF = ({ clientes }) => {
    const [isReady, setIsReady] = useState(false);
    return (
        <div onMouseEnter={() => setIsReady(true)} onClick={() => setIsReady(true)}>
            {isReady ? (
                <PDFDownloadLink document={<ClientesPDF clientes={clientes} />} fileName={`BD_Clientes_Scarjav_${new Date().toISOString().split('T')[0]}.pdf`} style={{ textDecoration: 'none' }}>
                    {({ loading }) => (
                        <motion.button whileTap={{ scale: 0.95 }} className={`flex items-center gap-2 text-sm font-bold h-9 px-3 rounded-md shadow-sm transition-colors ${loading ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 text-white'}`} disabled={loading}>
                            {loading ? <FaSpinner className="animate-spin" /> : <FaFilePdf />}
                            <span className="hidden sm:inline">{loading ? 'Preparando...' : 'Descargar Lista'}</span>
                        </motion.button>
                    )}
                </PDFDownloadLink>
            ) : (
                <motion.button whileTap={{ scale: 0.95 }} className="flex items-center gap-2 bg-red-500 text-white text-sm font-bold h-9 px-3 rounded-md shadow-sm hover:bg-red-600 transition-colors">
                    <FaFilePdf /> <span className="hidden sm:inline">Descargar Lista</span>
                </motion.button>
            )}
        </div>
    );
};

function ContactFormModal({ onSave, onClose, initialData = {} }) {
    const [contactName, setContactName] = useState(initialData.nombre || '');
    const [contactPhone, setContactPhone] = useState(initialData.telefono || '');
    const [contactEmail, setContactEmail] = useState(initialData.email || '');
    const [contactPosition, setContactPosition] = useState(initialData.cargo || '');
    
    const handleSubmit = (e) => { 
        e.preventDefault(); 
        if (!contactName) return toast.error("El nombre del contacto es obligatorio."); 
        onSave({ nombre: contactName, telefono: contactPhone, email: contactEmail, cargo: contactPosition }); 
        onClose(); 
    };
    
    const inputStyle = "mt-1 block w-full px-2 py-1.5 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#18BC9C] focus:border-[#18BC9C]";
    const labelStyle = "block text-xs font-medium text-gray-700";

    return (
        <motion.form initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }} onSubmit={handleSubmit} className="bg-white p-5 rounded-lg shadow-xl">
            <h3 className="text-lg font-bold text-gray-700 mb-3">{initialData.nombre ? 'Editar Contacto' : 'Añadir Nuevo Contacto'}</h3>
            <div className="space-y-3">
                <div><label className={labelStyle}>Nombre</label><input type="text" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Nombre del contacto" required className={inputStyle} /></div>
                <div><label className={labelStyle}>Teléfono</label><input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="600 123 456" className={inputStyle} /></div>
                <div><label className={labelStyle}>Email</label><input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="contacto@email.com" className={inputStyle} /></div>
                <div><label className={labelStyle}>Cargo (Opcional)</label><input type="text" value={contactPosition} onChange={e => setContactPosition(e.target.value)} placeholder="Ej: Gerente de Proyectos" className={inputStyle} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <motion.button whileTap={{ scale: 0.95 }} type="button" onClick={onClose} className="bg-gray-200 text-gray-700 text-sm font-bold h-9 px-4 rounded-md shadow-sm hover:bg-gray-300">Cancelar</motion.button>
                <motion.button whileTap={{ scale: 0.95 }} type="submit" className="bg-[#18BC9C] text-white text-sm font-bold h-9 px-4 rounded-md shadow-sm hover:bg-[#16a085] flex items-center justify-center gap-2"><FaPlus /> Guardar</motion.button>
            </div>
        </motion.form>
    );
}

function ClientesView() {
    const [clientes, setClientes] = useState([]);
    const [cargando, setCargando] = useState(true);
    
    // --- ESTADOS DEL FORMULARIO ---
    const [nombre, setNombre] = useState('');
    const [nif, setNif] = useState('');
    const [direccion, setDireccion] = useState('');
    const [codPostal, setCodPostal] = useState('');
    const [poblacion, setPoblacion] = useState('');
    
    const [editingClientId, setEditingClientId] = useState(null);
    const [personasContacto, setPersonasContacto] = useState([]);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [editingContactIndex, setEditingContactIndex] = useState(null);
    
    // Búsqueda
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchPerformed, setSearchPerformed] = useState(false);
    
    // Estilo compacto para inputs principales
    const inputStyle = "mt-1 block w-full px-2 py-1.5 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#18BC9C] focus:border-[#18BC9C]";
    
    useEffect(() => { 
        const q = query(collection(db, 'clientes')); 
        const unsubscribe = onSnapshot(q, (snapshot) => { 
            const clientesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })); 
            clientesData.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0)); 
            setClientes(clientesData); 
            setCargando(false); 
        }); 
        return () => unsubscribe(); 
    }, []);
    
    const resetForm = () => { 
        setNombre(''); 
        setNif(''); 
        setDireccion(''); 
        setCodPostal(''); 
        setPoblacion(''); 
        setPersonasContacto([]); 
        setEditingClientId(null); 
    };
    
    const handleEditClick = (cliente) => { 
        setEditingClientId(cliente.id); 
        setNombre(cliente.name || ''); 
        setNif(cliente.nif || ''); 
        setDireccion(cliente.direccion || ''); 
        setCodPostal(cliente.codPostal || ''); 
        setPoblacion(cliente.poblacion || ''); 
        setPersonasContacto(cliente.personasContacto || []); 
        window.scrollTo(0, 0); 
    };
    
    const handleSaveCliente = async (e) => { 
        e.preventDefault(); 
        if (!nombre) return toast.error('El nombre del cliente es obligatorio.'); 
        
        const clientData = { 
            name: nombre, 
            nif: nif, 
            direccion: direccion, 
            codPostal: codPostal, 
            poblacion: poblacion, 
            personasContacto: personasContacto, 
            status: 'Activo', 
        }; 
        
        const toastId = toast.loading(editingClientId ? 'Actualizando...' : 'Añadiendo...'); 
        try { 
            if (editingClientId) { 
                const clientDocRef = doc(db, 'clientes', editingClientId); 
                await updateDoc(clientDocRef, { ...clientData, updatedAt: serverTimestamp() }); 
                toast.success('¡Cliente actualizado!', { id: toastId }); 
            } else { 
                await addDoc(collection(db, 'clientes'), { ...clientData, createdAt: serverTimestamp() }); 
                toast.success('¡Cliente añadido!', { id: toastId }); 
            } 
            resetForm(); 
        } catch (error) { 
            console.error("Error al guardar cliente: ", error); 
            toast.error('No se pudo guardar el cliente.', { id: toastId }); 
        } 
    };
    
    const handleDeleteClient = async (clientId) => { if (window.confirm("¿Estás seguro de que quieres eliminar este cliente?")) { try { await deleteDoc(doc(db, 'clientes', clientId)); toast.success('Cliente eliminado.'); } catch (error) { toast.error('No se pudo eliminar el cliente.'); } } };
    const handleSaveContact = (contactData) => { if (editingContactIndex !== null) { setPersonasContacto(personasContacto.map((c, index) => index === editingContactIndex ? contactData : c)); } else { setPersonasContacto([...personasContacto, contactData]); } setEditingContactIndex(null); };
    const handleOpenContactModal = (index = null) => { setEditingContactIndex(index); setIsContactModalOpen(true); };
    const handleDeleteContact = (indexToDelete) => { setPersonasContacto(personasContacto.filter((_, index) => index !== indexToDelete)); };
    const handleSearch = () => { setSearchTerm(inputValue); setSearchPerformed(true); };
    const clearSearch = () => { setInputValue(''); setSearchTerm(''); setSearchPerformed(false); };
    
    const filteredClientes = clientes.filter(c => { 
        const searchTermLower = searchTerm.toLowerCase(); 
        const inClientName = (c.name || '').toLowerCase().includes(searchTermLower); 
        const inNif = (c.nif || '').toLowerCase().includes(searchTermLower); 
        const inDirection = (c.direccion || '').toLowerCase().includes(searchTermLower); 
        const inPoblacion = (c.poblacion || '').toLowerCase().includes(searchTermLower);
        const inContacts = c.personasContacto?.some(p => (p.nombre || '').toLowerCase().includes(searchTermLower) || (p.telefono || '').includes(searchTermLower) || (p.email || '').toLowerCase().includes(searchTermLower) ); 
        return inClientName || inDirection || inContacts || inNif || inPoblacion; 
    });

    const tableRowVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.2 } }),
        exit: { opacity: 0, x: -50, transition: { duration: 0.1 } }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col p-3 md:p-4 gap-3">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Gestión de Clientes</h1>
                {clientes.length > 0 && <BotonDescargaPDF clientes={searchPerformed ? filteredClientes : clientes} />}
            </div>

            <motion.div layout initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
                 <form onSubmit={handleSaveCliente}>
                    {/* 🔥 MODIFICACIÓN CRÍTICA: Grid de 5 columnas para pantallas grandes, ahorra 3 filas de espacio vertical */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                        <div><label className="block text-xs font-medium text-gray-700">Nombre Cliente</label><input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: Fontanería López" required className={inputStyle}/></div>
                        <div><label className="block text-xs font-medium text-gray-700">NIF / CIF</label><input type="text" value={nif} onChange={(e) => setNif(e.target.value)} placeholder="Ej: B12345678" className={inputStyle}/></div>
                        <div><label className="block text-xs font-medium text-gray-700">Dirección Fiscal</label><input type="text" value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Calle y número" className={inputStyle}/></div>
                        <div><label className="block text-xs font-medium text-gray-700">Código Postal</label><input type="text" value={codPostal} onChange={(e) => setCodPostal(e.target.value)} placeholder="28001" className={inputStyle}/></div>
                        <div><label className="block text-xs font-medium text-gray-700">Población / Ciudad</label><input type="text" value={poblacion} onChange={(e) => setPoblacion(e.target.value)} placeholder="Madrid" className={inputStyle}/></div>
                    </div>
                    
                    <div className="mt-3 border-t pt-3">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
                            <h4 className="text-sm font-bold text-gray-700">Personas de Contacto</h4>
                            <motion.button whileTap={{scale: 0.95}} type="button" onClick={() => handleOpenContactModal()} className="w-full sm:w-auto flex items-center justify-center gap-1 bg-gray-600 text-white text-xs font-semibold px-2 py-1.5 rounded-md hover:bg-gray-700"><FaUserPlus /> Añadir Contacto</motion.button>
                        </div>
                        <div className="space-y-1.5">
                            <AnimatePresence>
                                {personasContacto.map((p, index) => (
                                    <motion.div key={p.email || index} initial={{opacity: 0, height: 0}} animate={{opacity: 1, height: 'auto'}} exit={{opacity: 0, height: 0}} transition={{ duration: 0.2 }} layout className="flex flex-col sm:flex-row justify-between sm:items-center bg-gray-50 p-1.5 rounded-md gap-2 border border-gray-100">
                                        <div><p className="font-semibold text-gray-800 text-sm">{p.nombre} {p.cargo && <span className="text-xs text-gray-500 font-normal">({p.cargo})</span>}</p><p className="text-xs text-gray-600 break-all">{p.telefono} - {p.email}</p></div>
                                        <div className="flex gap-2 self-end sm:self-center">
                                            <motion.button whileTap={{scale: 0.9}} type="button" onClick={() => handleOpenContactModal(index)} className="text-gray-400 hover:text-blue-600 p-1" title="Editar Contacto"><FaUserEdit size={14}/></motion.button>
                                            <motion.button whileTap={{scale: 0.9}} type="button" onClick={() => handleDeleteContact(index)} className="text-gray-400 hover:text-red-600 p-1" title="Eliminar Contacto"><FaTrash size={14}/></motion.button>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-3 border-t pt-3">
                        {editingClientId && (<motion.button whileTap={{scale: 0.95}} type="button" onClick={resetForm} className="bg-gray-200 text-gray-700 text-sm font-bold h-8 px-4 rounded-md shadow-sm hover:bg-gray-300">Cancelar</motion.button>)}
                        <motion.button whileTap={{scale: 0.95}} type="submit" className="bg-[#18BC9C] text-white text-sm font-bold h-8 px-4 rounded-md shadow-sm hover:bg-[#16a085] flex items-center justify-center gap-2"><FaPlus />{editingClientId ? 'Actualizar' : 'Guardar'}</motion.button>
                    </div>
                </form>
            </motion.div>
            
            <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center gap-2">
                <div className="relative flex-grow w-full"><span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FaSearch className="text-gray-400 text-sm" /></span><input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} placeholder="Buscar cliente..." className="block w-full pl-9 pr-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#18BC9C] focus:border-[#18BC9C]"/></div>
                <motion.button whileTap={{scale: 0.95}} onClick={handleSearch} className="w-full sm:w-auto flex justify-center items-center gap-1 bg-[#2C3E50] text-white text-sm font-bold h-8 px-4 rounded-md shadow-sm hover:bg-gray-700"><FaSearch size={12}/> <span className="sm:hidden md:inline">Buscar</span></motion.button>
                <motion.button whileTap={{scale: 0.95}} onClick={clearSearch} className="w-full sm:w-auto flex justify-center items-center gap-1 bg-gray-200 text-gray-700 text-sm font-bold h-8 px-4 rounded-md shadow-sm hover:bg-gray-300"><FaTimes size={12}/> <span className="sm:hidden md:inline">Limpiar</span></motion.button>
            </div>

            {searchPerformed ? (
                <motion.div layout className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-x-auto flex-grow">
                    <table className="min-w-full leading-normal">
                        <thead><tr><th className="px-4 py-2 border-b-2 border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cliente</th><th className="px-4 py-2 border-b-2 border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contacto Principal</th><th className="px-4 py-2 border-b-2 border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Dirección</th><th className="px-4 py-2 border-b-2 border-gray-200 bg-gray-50"></th></tr></thead>
                        <tbody>
                            <AnimatePresence>
                                {cargando ? (<tr><td colSpan="4" className="text-center py-4 text-sm">Buscando...</td></tr>) : ( filteredClientes.length > 0 ? ( filteredClientes.map((cliente, i) => ( 
                                    <motion.tr 
                                        key={cliente.id} 
                                        custom={i} 
                                        variants={tableRowVariants} 
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                        layout="position"
                                        className="hover:bg-teal-50"
                                    >
                                        <td className="px-4 py-3 border-b border-gray-200 text-sm"><p className="text-gray-900 whitespace-no-wrap font-semibold">{cliente.name}</p><p className="text-gray-500 whitespace-no-wrap text-xs">{cliente.nif || 'Sin NIF'}</p></td>
                                        <td className="px-4 py-3 border-b border-gray-200 text-sm"><p className="text-gray-900 whitespace-no-wrap">{cliente.personasContacto?.[0]?.nombre || 'N/A'}</p><p className="text-gray-500 whitespace-no-wrap text-xs">{cliente.personasContacto?.[0]?.telefono || ''}</p>{cliente.personasContacto?.length > 1 && <p className="text-xs text-blue-500 italic mt-0.5">y {cliente.personasContacto.length - 1} más...</p>}</td>
                                        <td className="px-4 py-3 border-b border-gray-200 text-sm">
                                            <p className="text-gray-900 whitespace-no-wrap">{cliente.direccion}</p>
                                            {(cliente.codPostal || cliente.poblacion) && <p className="text-gray-500 text-xs mt-0.5">{cliente.codPostal} {cliente.poblacion}</p>}
                                        </td>
                                        <td className="px-4 py-3 border-b border-gray-200 text-sm text-right">
                                            <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleEditClick(cliente)} className="text-gray-400 hover:text-indigo-600 mr-3" title="Editar Cliente"><FaPencilAlt size={14}/></motion.button>
                                            <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleDeleteClient(cliente.id)} className="text-gray-400 hover:text-red-600" title="Eliminar Cliente"><FaTrash size={14}/></motion.button>
                                        </td>
                                    </motion.tr>
                                )) ) : (<tr><td colSpan="4" className="text-center py-4 text-sm text-gray-500">No se encontraron clientes.</td></tr>) )}
                            </AnimatePresence>
                        </tbody>
                    </table>
                </motion.div>
            ) : ( <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-grow flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-100"><p className="text-gray-400 text-sm text-center p-4">Usa la barra de búsqueda para encontrar un cliente.</p></motion.div> )}

            <AnimatePresence>
                {isContactModalOpen && (
                    <Modal isOpen={true} onRequestClose={() => setIsContactModalOpen(false)} style={customModalStyles} ariaHideApp={false}>
                        <ContactFormModal 
                            onSave={handleSaveContact} 
                            onClose={() => setIsContactModalOpen(false)} 
                            initialData={editingContactIndex !== null ? personasContacto[editingContactIndex] : {}}
                        />
                    </Modal>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default ClientesView;